import {stringify} from "comment-json";
import {omit, startCase} from "lodash";
import {ElementsTree} from "../../../../model/ElementsTree";
import {Graph, makeNodeFromJs, Node, NodeId, NodeType} from "../../../../model/Graph";
import {Playshot} from "../../../../model/Playshot";
import {makeAsyncIterable} from "../../../async-iterable/make-async-iterable";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {nodeLabel, removeDefaultService} from "../../default-service";
import {getNextGeo} from "../graph-manipulation";
import {
  PlayshotApplyContext,
  PlayshotChanged,
  PlayshotChangedDiff,
  PlayshotChangedOperation,
  PlayshotChangedType
} from "../PlayshotChanged";
import {PlayInformationRuntimeInfo} from "./information-definitions";
import {prepareDiffInformation, PreparedInformation} from "./prepare-diff-information";

export class InformationAdded implements PlayshotChanged {
  public readonly name: string;
  public readonly type: PlayshotChangedType = 'NewInformation';
  public readonly operation: PlayshotChangedOperation = 'added';
  public readonly nodeId: NodeId;
  public readonly nodeType = NodeType.document;
  public readonly diff: PlayshotChangedDiff;

  private newInformation: PreparedInformation;

  constructor(name: string, nodeId: string, vo: PlayInformationRuntimeInfo, playshot: Playshot) {
    this.name = name;
    this.nodeId = nodeId;
    this.newInformation = prepareDiffInformation(vo, playshot);
    this.diff = {
      new: this.newInformation,
      old: null,
    };

  }

  public async apply({syncContext, graph, playshot, addedNodes, elementsTree}: PlayshotApplyContext): Promise<AsyncIterable<CodyResponse>> {
    if(addedNodes[this.nodeId]) {
      return makeAsyncIterable({
        cody: ``,
        type: CodyResponseType.Empty
      })
    }

    const existingNode = graph.getNode(this.nodeId);

    if(existingNode) {
      return makeAsyncIterable({
        cody: `Hhm, I'm skipping the change "${startCase(this.type)}: ${removeDefaultService(this.name, playshot)}", because the information seems to exist already on the board.`,
        details: `There is at least an element on the board with ID: ${this.nodeId}`,
        type: CodyResponseType.Warning
      })
    }


    const node = makeNodeFromJs({
      id: this.nodeId,
      type: NodeType.document,
      name: nodeLabel(this.name),
      metadata: stringify(omit({
        ...this.newInformation.desc,
        schema: this.newInformation.schema,
        uiSchema: this.newInformation.uiSchema,
        querySchema: this.newInformation.querySchema,
        resolve: this.newInformation.resolve,
        projection: this.newInformation.projection,
        initialize: this.newInformation.initialize,
      }, [
        "name",
      ]))
    })

    graph.addNode(node);
    graph.switchParent(node, syncContext, getNextGeo(addedNodes), true);
    graph.focusNode(node);

    addedNodes[node.getId()] = graph.getNode(node.getId()) || node;

    return makeAsyncIterable({
      cody: `I've added an information card for "${nodeLabel(this.name)}" to the board`
    });
  }
}
